/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The receiving half of recipe sharing, plus the sender's ledger.
 *
 * ── WHY PREVIEW IS A SEPARATE STEP ──────────────────────────────────────────
 * Accepting writes n recipes into the household library, and un-doing that is n
 * manual archives. So the code buys a LOOK first: what's in the bundle, who
 * sent it, and — the part that actually matters — what allergens it carries,
 * checked against the family's own eaters before anything is written.
 *
 * The preview deliberately shows ingredient NAMES but not the method. It is
 * enough to answer "do we want this" and "can we eat this" without making the
 * preview a way to read someone's recipes without accepting them.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { SharesApi } from "../../api/endpoints.ts";
import { AllergenBadge, Pill, Spinner } from "../../components/ui.tsx";
import { useToast } from "../../components/Toast.tsx";
import type { IncomingShare, OutgoingShare, SharePreview } from "../../api/types.ts";

/**
 * `days` is SERVER-computed (`expiresInDays`), never derived here.
 *
 * A browser clock an hour behind midnight would otherwise render "expires in
 * 2 days" for a code the server will refuse tomorrow. Same reasoning as
 * lib/dates.ts: this file never asks the device what time it is.
 */
function expiryLabel(days: number): string {
  if (days <= 0) return "expires today";
  return days === 1 ? "expires tomorrow" : `expires in ${days} days`;
}

export function SharedRecipesPanel({ householdAllergens }: {
  /** The combined allergen set across this household's eaters. */
  householdAllergens: string[];
}) {
  const toast = useToast();

  const [code, setCode] = useState("");
  const [preview, setPreview] = useState<SharePreview | null>(null);
  const [outgoing, setOutgoing] = useState<OutgoingShare[]>([]);
  const [received, setReceived] = useState<IncomingShare[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  /** Reset on every new preview: it is a decision about THIS bundle. */
  const [joinDiscussion, setJoinDiscussion] = useState(false);

  const loadLedger = useCallback(async () => {
    try {
      const [o, r] = await Promise.all([SharesApi.list(), SharesApi.received()]);
      setOutgoing(o.shares);
      setReceived(r.received);
    } catch { /* the ledger is informational; a failure here isn't worth a toast */ }
  }, []);

  useEffect(() => { void loadLedger(); }, [loadLedger]);

  async function look() {
    setBusy("preview");
    setPreview(null);
    setJoinDiscussion(false);
    try {
      setPreview(await SharesApi.preview(code.trim()));
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't read that code.", "danger");
    }
    setBusy(null);
  }

  async function accept() {
    setBusy("accept");
    try {
      const out = await SharesApi.accept(code.trim(), joinDiscussion);
      const n = out.imported.length;
      toast(
        n === 0
          ? "You already had all of those."
          : `Added ${n} recipe${n === 1 ? "" : "s"} from ${out.fromName}`
            + (out.discussionJoined ? " — you've joined the discussion." : ""),
        "ok",
      );
      setPreview(null);
      setCode("");
      setJoinDiscussion(false);
      await loadLedger();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't add those recipes.", "danger");
    }
    setBusy(null);
  }

  async function revoke(id: string) {
    if (!confirm(
      "Revoke this code? Families who already used it keep their copies — " +
      "this only stops new ones.",
    )) return;
    setBusy(`revoke-${id}`);
    try {
      await SharesApi.revoke(id);
      toast("Revoked", "ok");
      await loadLedger();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't revoke that.", "danger");
    }
    setBusy(null);
  }

  const allergenSet = new Set(householdAllergens.map((a) => a.toLowerCase()));

  return (
    <section className="card p-5 md:col-span-2">
      <div className="mb-1 label-mono">Recipes from other families</div>
      <p className="mb-4 text-xs text-ink-3">
        Someone can send you a code from their recipe list. You get your own copy to edit —
        nothing stays linked to them afterwards.
      </p>

      {/* ── redeem ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2">
        <input
          className="input font-mono uppercase tracking-widest sm:max-w-[14rem]"
          placeholder="XXXX-XXXX"
          value={code}
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setPreview(null); }}
        />
        <button className="btn-ghost shrink-0" disabled={busy !== null || code.trim().length < 4}
                onClick={() => void look()}>
          {busy === "preview" ? <Spinner className="h-4 w-4" /> : "Look inside"}
        </button>
      </div>

      {preview && (
        <div className="mt-4 rounded-lg border border-rule-soft bg-paper-2 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="text-sm text-ink">
              <strong>{preview.fromName}</strong> sent {preview.recipes.length}{" "}
              recipe{preview.recipes.length === 1 ? "" : "s"}
            </div>
            <span className="text-xs text-ink-3">{expiryLabel(preview.expiresInDays)}</span>
          </div>
          {preview.message && (
            <p className="mt-1 text-sm italic text-ink-2">“{preview.message}”</p>
          )}

          <ul className="mt-3 space-y-2">
            {preview.recipes.map((r) => {
              // Checked against THIS household's eaters, before anything is
              // written. The strongest claim made is "no known conflicts" —
              // an unidentified ingredient reads as "may contain", never safe.
              const clash = r.allergenTags.filter((a) => allergenSet.has(a.toLowerCase()));
              return (
                <li key={r.id} className="rounded border border-rule-soft bg-paper p-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-medium text-ink">{r.title}</span>
                    <span className="text-xs text-ink-3">
                      {r.totalMinutes ? `${r.totalMinutes} min · ` : ""}
                      {r.servingsBase} {r.servingLabel} · {r.ingredientCount} ingredients
                    </span>
                  </div>
                  {r.description && (
                    <p className="mt-1 line-clamp-2 text-sm text-ink-3">{r.description}</p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-1">
                    <AllergenBadge
                      tags={r.allergenTags}
                      partial={r.allergensPartial}
                      conflictsWith={clash.length ? clash : undefined}
                    />
                    {r.cuisine && <Pill>{r.cuisine}</Pill>}
                    {r.dietTags.map((d) => <Pill key={d}>{d}</Pill>)}
                  </div>
                  {r.ingredientNames.length > 0 && (
                    <p className="mt-2 line-clamp-2 text-xs text-ink-3">
                      {r.ingredientNames.join(", ")}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>

          {preview.duplicateCount > 0 && (
            <p className="mt-3 text-xs text-ink-3">
              {preview.duplicateCount} of these {preview.duplicateCount === 1 ? "is" : "are"}{" "}
              already in your library and won't be added twice.
            </p>
          )}

          {/* Only offered when the SENDER opened it. Unticked by default, and
              declining is not final — the recipe page offers it again later. */}
          {preview.allowsDiscussion && !preview.isOwnShare && (
            <label className="mt-4 flex items-start gap-2 rounded-lg border border-rule-soft
                              bg-paper-2 p-3">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={joinDiscussion}
                onChange={(e) => setJoinDiscussion(e.target.checked)}
              />
              <span className="text-sm">
                <span className="font-medium text-ink">Join the discussion too</span>
                <span className="mt-0.5 block text-xs text-ink-3">
                  {preview.fromName} opened a comment thread on these recipes. Anything
                  you write there is visible to every family that has them. Your own
                  notes and meal plans stay private, and you can join or leave later.
                </span>
              </span>
            </label>
          )}

          <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
            {preview.isOwnShare && (
              <span className="mr-auto text-xs text-ink-3">This is your own code.</span>
            )}
            {preview.alreadyAccepted && (
              <span className="mr-auto text-xs text-ink-3">You've already added these.</span>
            )}
            <button className="btn-ghost" onClick={() => setPreview(null)}>Not now</button>
            <button
              className="btn-primary"
              disabled={busy !== null || preview.isOwnShare || preview.alreadyAccepted}
              onClick={() => void accept()}
            >
              {busy === "accept" ? <Spinner className="h-4 w-4" /> : "Add to my recipes"}
            </button>
          </div>
        </div>
      )}

      {/* ── ledgers ────────────────────────────────────────────────────── */}
      <div className="mt-6 grid gap-6 border-t border-rule-soft pt-4 sm:grid-cols-2">
        <div>
          <div className="label-mono mb-2">Codes you've sent</div>
          {outgoing.length === 0 ? (
            <p className="text-xs text-ink-3">
              None yet. Pick recipes in{" "}
              <Link to="/recipes" className="text-accent hover:underline">your library</Link>{" "}
              and choose “Select to share”.
            </p>
          ) : (
            <ul className="space-y-2">
              {outgoing.map((s) => {
                const dead = s.revoked || s.expired || s.uses >= s.maxUses;
                return (
                  <li key={s.id} className="flex items-start justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {/* Only the last 4 characters exist in clear. The code
                            itself was never stored and can't be re-shown. */}
                        <span className="font-mono text-xs text-ink-3">···{s.codeHint}</span>
                        <span className="truncate text-ink-2">
                          {s.recipeCount} recipe{s.recipeCount === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div className="text-xs text-ink-3">
                        {s.revoked ? "revoked"
                          : s.expired ? "expired"
                            : `used ${s.uses}/${s.maxUses} · ${expiryLabel(s.expiresInDays)}`}
                        {s.recipeTitles.length > 0 && ` · ${s.recipeTitles.join(", ")}`}
                      </div>
                    </div>
                    {!dead && (
                      <button className="shrink-0 px-1 text-ink-3 hover:text-danger"
                              title="Revoke this code"
                              disabled={busy === `revoke-${s.id}`}
                              onClick={() => void revoke(s.id)}>✕</button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div>
          <div className="label-mono mb-2">Recipes you've received</div>
          {received.length === 0 ? (
            <p className="text-xs text-ink-3">Nothing yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {received.map((r) => (
                <li key={r.id}>
                  <span className="text-ink-2">
                    {r.recipeCount} from <strong>{r.fromName}</strong>
                  </span>
                  {r.message && (
                    <span className="block text-xs italic text-ink-3">“{r.message}”</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
